/**
 * Elimination Standard Style — Clean section renderers for GridEngine
 * 
 * Exports:
 * - EliminationStandardHeroSection: Clean card with partner logo, title
 * - EliminationStandardLiveDrawSection: Grand prize with progress bar, remaining tickets, countdown, CTA
 */

import React, { useState } from 'react'
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Target from 'lucide-react/dist/esm/icons/target.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'
import { useContent } from '../../../contexts/ContentContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'
import CountdownTimerStyles from '../../../components/CountdownTimerStyles'
import { useCountdown } from '../../../hooks/useCountdown'
import { formatCurrencyValue } from '../../../utils/formatCurrency'
import { useTranslations } from '../../../contexts/TranslationContext'

/**
 * Hero Section — Clean card with partner logo and title
 */
export function EliminationStandardHeroSection({
  partner,
  editMode,
  handleTranslationUpdate,
  onLoginClick,
}) {
  const { c } = useContent()
  const { t: tr } = useTranslations()

  return (
    <div className="text-center md:col-span-12">
      {partner?.partner_logo_url && (
        <div className="flex justify-center mb-4">
          <img
            src={partner.partner_logo_url}
            alt={partner.partner_name || tr('common.partnerLogo', 'Partner Logo')}
            className="h-16 object-contain"
          />
        </div>
      )}
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-2">
        <InlineTranslationEdit
          translationKey="elimination.title"
          value={c('elimination.title', 'Elimination Draw')}
          fallback="Elimination Draw"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </h1>
      <p className="text-gray-600 dark:text-gray-400">
        <InlineTranslationEdit
          translationKey="elimination.tagline"
          value={c('elimination.tagline', 'Stay in the game. Be the last ticket standing.')}
          fallback="Stay in the game. Be the last ticket standing."
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </p>
      <HeroPartnerGlassSelect className="mx-auto mt-6 w-full max-w-md" />
    </div>
  )
}

/**
 * Live Draw Section — Grand prize with progress bar (red), remaining tickets, countdown, CTA
 */
export function EliminationStandardLiveDrawSection({
  mainDraw,
  loading,
  editMode,
  handleTranslationUpdate,
  onLoginClick,
  countdownStyle, language,
}) {
  const { c } = useContent()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const { timeLeft, shouldAnimate } = useCountdown(
    mainDraw?.next_elimination_date || mainDraw?.draw_date,
    { enableAnimation: true }
  )

  // Calculate values — use ?? so 0 is treated as valid
  const totalTickets = mainDraw?.total_tickets ?? 0
  const remainingTickets = mainDraw?.remaining_tickets ?? 0
  const eliminatedCount = totalTickets - remainingTickets
  const grandPrize = mainDraw?.current_jackpot ?? mainDraw?.grand_prize ?? 0
  const progressPercentage = totalTickets > 0 ? ((eliminatedCount / totalTickets) * 100).toFixed(1) : '0.0'

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
        <div className="text-gray-500 dark:text-gray-400">{tr('elimination.loadingDraw', 'Loading draw information...')}</div>
      </div>
    )
  }

  if (!mainDraw) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
        <p className="text-gray-600 dark:text-gray-400">{tr('elimination.noActiveDraws', 'No active elimination draws at the moment.')}</p>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 text-center md:col-span-12">
      {/* Grand Prize */}
      <div className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
        <InlineTranslationEdit
          translationKey="elimination.grandPrizeLabel"
          value={c('elimination.grandPrizeLabel', 'Grand Prize')}
          fallback="Grand Prize"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </div>
      <div className="text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 dark:text-white mb-6">
        {formatCurrencyValue(grandPrize, { language })}
      </div>

      {/* Progress Bar (Red Theme) */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
            <InlineTranslationEdit
              translationKey="elimination.progress"
              value={c('elimination.progress', 'Elimination Progress')}
              fallback="Elimination Progress"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </span>
          <span className="text-sm font-bold text-red-600 dark:text-red-400">
            {progressPercentage}% <InlineTranslationEdit translationKey="elimination.eliminated" value={c('elimination.eliminated', 'eliminated')} fallback="eliminated" onUpdate={handleTranslationUpdate} editMode={editMode} />
          </span>
        </div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-red-600 to-red-400 transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Remaining Tickets */}
      <div className="flex items-center justify-center gap-2 text-gray-700 dark:text-gray-300 mb-4">
        <Target className="w-5 h-5 text-red-600" />
        <span className="text-lg font-bold">{remainingTickets.toLocaleString()}</span>
        <span>
          <InlineTranslationEdit
            translationKey="elimination.ticketsRemaining"
            value={c('elimination.ticketsRemaining', 'tickets remaining')}
            fallback="tickets remaining"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </span>
      </div>

      {/* Countdown */}
      {(mainDraw?.next_elimination_date || mainDraw?.draw_date) && (
        <div className="mb-6">
          <div className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            <InlineTranslationEdit
              translationKey="elimination.nextElimination"
              value={c('elimination.nextElimination', 'Next Elimination In')}
              fallback="Next Elimination In"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </div>
          <CountdownTimerStyles
            timeLeft={timeLeft}
            shouldAnimate={shouldAnimate}
            style={countdownStyle}
          />
        </div>
      )}

      {/* CTA Button */}
      <button
        onClick={() => purchaseNavigate('/purchase')}
        className="inline-flex items-center gap-3 px-8 py-4 bg-red-600 hover:bg-red-700 text-white text-lg font-bold rounded-xl transition-all hover:scale-105 shadow-lg hover:shadow-xl"
      >
        <Ticket className="w-5 h-5" />
        <InlineTranslationEdit
          translationKey="elimination.buyTickets"
          value={c('elimination.buyTickets', 'Buy Tickets Now')}
          fallback="Buy Tickets Now"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </button>
    </div>
  )
}
